import Collapsible from '../shared/Collapsible.js';
import '../broiler/poultryDesign.css';
import Modal, {ModalFooter} from '../shared/Modal.js';
import Button from '../shared/Button.js';
// Phase 2 Round 3 extraction (verbatim).
import React from 'react';
import {openableProps} from '../shared/openable.js';
import {S, getReadableText} from '../lib/styles.js';
import {getProgramColor} from '../lib/programColors.js';

import InlineNotice from '../shared/InlineNotice.jsx';

import Badge from '../shared/Badge.jsx';
import {GroupDot} from '../shared/GroupPillPicker.js';
import {buildLayerGroupPalette, layerGroupColor, layerGroupTint} from './layerGroupPalette.js';
import {fmtDate as fmt} from '../lib/dateUtils.js';
import Header from '../shared/Header.tsx';
import {sb} from '../lib/supabase.js';
import {
  LAYER_GROUPS_CONFLICT_MESSAGE,
  reportAppStoreSaveStatus,
  saveLayerGroupsDocument,
} from '../lib/appStoreOperationApi.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import {useLayer} from '../contexts/LayerContext.tsx';
import {
  filterActiveLayerGroups,
  isActiveLayerGroupStatus,
  layerGroupStatusLabel,
  normalizeLayerGroupStatus,
  LAYER_GROUP_STATUS_ACTIVE,
  LAYER_GROUP_STATUS_OPTIONS,
} from '../lib/layerGroupStatus.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {LayerGroupsCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {useLayerGroupBlobEviction} from '../app/data/layerGroupBlobCache.js';
import {useConfirmedDraftAutosave} from '../shared/useConfirmedDraftAutosave.js';
import type {CSSProperties} from 'react';
import type {Notice} from '../shared/InlineNotice.js';

// A legacy layer-group row out of the ppp-layer-groups-v1 app_store blob,
// reduced to the fields this view reads. Every field is optional and the seven
// numeric ones are `string | number` because BOTH shapes really are in the
// blob: `save()` below writes them through parseInt/parseFloat (numbers), while
// rows written before that normalisation existed still carry the raw strings.
// No field is `| null`, and that is derived rather than assumed: the ONLY
// writers of these rows are save() / updLayer() / closeLayerForm() below, and
// all three build the row by spreading a form whose fields are strings or
// parse* results. A key can be ABSENT on a row written before a field existed,
// which is what the `?` records; it is never explicitly null.
type LayerGroupRecord = {
  id?: string | number;
  name?: string;
  status?: string;
  house?: string;
  startDate?: string;
  lastCountDate?: string;
  notes?: string;
  originalCount?: string | number;
  currentCount?: string | number;
  feedLbs?: string | number;
  gritLbs?: string | number;
  totalEggs?: string | number;
  perLbFeedCost?: string | number;
  perLbGritCost?: string | number;
};

// layer_housings / layer_batches rows, again only the columns read here.
type LayerHousingRecord = {
  batch_id?: string | null;
  housing_name?: string | null;
  status?: string | null;
  current_count?: number | null;
};
type LayerBatchRecord = {id?: string | null; name?: string | null};

// The eight columns the layer_dailys select below actually names.
type LayerDailyRow = {
  batch_label?: string | null;
  feed_lbs?: number | null;
  grit_lbs?: number | null;
  layer_count?: number | null;
  date?: string | null;
  team_member?: string | null;
  comments?: string | null;
  mortality_count?: number | null;
};

// The edit form. Distinct from LayerGroupRecord on purpose: EMPTY_LG seeds
// every field with '', and openEdit spreads a stored row over that seed, so a
// form field holds the row's number when there is one and '' when there is not.
type LayerGroupForm = {
  name: string;
  status: string;
  house: string;
  startDate: string;
  lastCountDate: string;
  notes: string;
  originalCount: string | number;
  currentCount: string | number;
  feedLbs: string | number;
  gritLbs: string | number;
  totalEggs: string | number;
  perLbFeedCost: string | number;
  perLbGritCost: string | number;
};

// The one form→row coercion, shared by every writer below (create, explicit
// edit save, confirmed autosave persist). Same parseInt/parseFloat defaults the
// three former inline copies used; identical rows, one place.
function buildLayerGroupRow(form: LayerGroupForm, id: LayerGroupRecord['id']): LayerGroupRecord {
  return {
    ...form,
    id,
    originalCount: parseInt(String(form.originalCount)) || 0,
    currentCount: parseInt(String(form.currentCount)) || 0,
    feedLbs: parseFloat(String(form.feedLbs)) || 0,
    gritLbs: parseFloat(String(form.gritLbs)) || 0,
    totalEggs: parseFloat(String(form.totalEggs)) || 0,
    perLbFeedCost: parseFloat(String(form.perLbFeedCost)) || 0,
    perLbGritCost: parseFloat(String(form.perLbGritCost)) || 0,
  };
}

// THE GROUPS PAGE NOW OWNS ITS WRITE AS WELL AS ITS FOUR READS.
//
// The ppp-layer-groups-v1 blob and the two layer directories used to arrive as
// LayerEntry props from App's former-eager boot transaction. layer.groups.core
// now owns all four render inputs, and the write takes the same route-local
// direction: saveLayerGroupsDocument is the audited server operation writer
// extracted into one shared transport seam.
//
// setLayerGroups remains a publish-only mirror for App's still-legacy
// feed-cost fan-out. No Layer Groups render value reads it: the view uses its
// route resource plus the local optimistic override below. The independent
// maintenance worker re-derives webform_config mirrors from stored source data,
// so this route neither reaches App's triggerMaintenance closure nor needs a
// legacy all-data claim to persist a group.
const LAYER_GROUPS_PARAMS = Object.freeze({});

const LayersView = () => {
  // The header's Saving / Saved / Error indicator. This is the exact sink App's
  // sbSave delegate uses, so a released route retains the same retry and
  // operator-visible failure posture rather than silently becoming write-only.
  const {setSaveStatus} = useAuth();
  // This is a WRITE into the legacy mirror, not a read from it. App's remaining
  // feed-cost fan-out still needs the most recently saved document when a later
  // legacy-only action runs, while every Layer Groups render value comes from
  // layer.groups.core below. The provenance guard deliberately scores setters
  // as non-consuming for this reason.
  const {setLayerGroups} = useLayer();
  const groupsResource = useRouteResource(RESOURCE.LAYER_GROUPS_CORE, LAYER_GROUPS_PARAMS);
  const groupsData = groupsResource.data as LayerGroupsCoreData | null;
  const {useState, useRef} = React;
  // The optimistic override. App's context write used to repaint this list
  // synchronously; a query result cannot. So a save publishes its next array
  // here as well as into the context mirror, and once it has, it wins for the
  // rest of this mount. B5c: that publish happens from the CONFIRMED callback
  // of the shared seam — a failed app_store write no longer repaints the list
  // with a value the server refused, and a remount re-reads from the server.
  const [savedGroups, setSavedGroups] = useState<LayerGroupRecord[] | null>(null);
  // The one place this view states what it believes the three opaque arrays
  // hold. Asserting once here, rather than at each of the ~30 field reads
  // below, keeps the claim reviewable and keeps the reads unannotated.
  const layerGroups = savedGroups ?? ((groupsData?.layerGroups || []) as LayerGroupRecord[]);
  const layerBatches = (groupsData?.layerBatches || []) as LayerBatchRecord[];
  const layerHousings = (groupsData?.layerHousings || []) as LayerHousingRecord[];
  const recentDailys = (groupsData?.recentDailys || []) as LayerDailyRow[];
  // Six resources project this one document. The local override and the legacy
  // context mirror below repaint THIS page; they do nothing for /dailys,
  // /layer/dailys, the batch record pages or either Home, which read their own
  // retained copies for five minutes. Dropping those entries the moment the
  // write lands - and before the header says Saved - is what stops a revisit
  // from painting a group this page has already switched off.
  const evictLayerGroupBlobReads = useLayerGroupBlobEviction();
  // B5c confirmed persistence: the write goes through the same shared seam,
  // but the saved overlay and the legacy mirror publish ONLY from the
  // onPersisted callback — the one the seam runs once the write is confirmed —
  // exactly as LayerBatchPage's retirement-home write already does. The
  // returned promise is the write's verdict; a refusal publishes nothing.
  // Review F3: the row is spliced into a FRESH read of the document (the same
  // read-before-write LayerBatchPage uses), because a whole-document upsert
  // built from this mount's snapshot would silently resurrect another tab's
  // committed changes.
  const saveLayerGroupsRow = async (row: LayerGroupRecord, isNew: boolean): Promise<boolean> => {
    setSaveStatus('saving');
    let next: LayerGroupRecord[] = [];
    const onLayerGroupsPersisted = () => {
      evictLayerGroupBlobReads();
      setLayerGroups(next);
      setSavedGroups(next);
    };
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const refreshed = (await groupsResource.refetch()) as {data?: LayerGroupsCoreData; error?: unknown} | undefined;
      if (refreshed?.error || !Array.isArray(refreshed?.data?.layerGroups)) {
        reportAppStoreSaveStatus(setSaveStatus, 'error');
        return false;
      }
      const fresh = refreshed.data.layerGroups as LayerGroupRecord[];
      if (!isNew && !fresh.some((g) => g.id === row.id)) {
        // The row vanished underneath the editor; refuse rather than resurrect.
        // Review R3: the notice offers Discard, because editing a ghost row
        // cannot succeed.
        vanishedRef.current = true;
        reportAppStoreSaveStatus(setSaveStatus, 'error');
        return false;
      }
      // Review N2: replace-or-append BY ID. A false-fail create retried with
      // its stable id finds its own row in the fresh read and replaces it
      // instead of appending a second group.
      next = fresh.some((g) => g.id === row.id) ? fresh.map((g) => (g.id === row.id ? row : g)) : [...fresh, row];
      const outcome = await saveLayerGroupsDocument(sb, {
        expectedUpdatedAt: refreshed.data.layerGroupsUpdatedAt ?? null,
        document: next,
      });
      if (outcome.status === 'saved' || outcome.status === 'no_change') {
        onLayerGroupsPersisted();
        reportAppStoreSaveStatus(setSaveStatus, 'saved');
        return true;
      }
      if (outcome.status === 'conflict' && attempt === 0) {
        continue;
      }
      if (outcome.status === 'conflict') {
        setNotice({kind: 'error', message: LAYER_GROUPS_CONFLICT_MESSAGE});
      }
      reportAppStoreSaveStatus(setSaveStatus, 'error');
      return false;
    }
    return false;
  };
  const EMPTY_LG: LayerGroupForm = {
    name: '',
    status: LAYER_GROUP_STATUS_ACTIVE,
    house: '',
    startDate: '',
    originalCount: '',
    currentCount: '',
    lastCountDate: '',
    feedLbs: '',
    gritLbs: '',
    totalEggs: '',
    perLbFeedCost: '',
    perLbGritCost: '',
    notes: '',
  };
  const [showForm, setShowForm] = useState(false);
  // Group ids are `Date.now().toString()` when this view creates them, but the
  // blob also carries ids written before that, so the edit handle tracks the
  // record's own id type. `undefined` is in the union because openEdit passes
  // `g.id` straight through: a row with no id would set the handle to undefined,
  // and `g.id === editId` then still matches that row the way it always has.
  // Coercing to null here would silently change which row the form is bound to.
  const [editId, setEditId] = useState<LayerGroupRecord['id'] | null>(null);
  const [form, setForm] = useState<LayerGroupForm>(EMPTY_LG);
  const formRef = useRef<LayerGroupForm>(EMPTY_LG);
  // Review F2: create-mode in-flight guard. The footer's busy state reads the
  // edit controller, which is idle in create mode, so without this a second
  // click sends a second whole-document write with a different id.
  const creatingRef = useRef(false);
  const [creating, setCreating] = useState(false);
  // Review N2: the create id is minted ONCE per open form and reused by every
  // retry, so a false-fail create retried after its write actually landed
  // replaces its own row (replace-or-append by id in saveLayerGroupsRow)
  // instead of appending a second group.
  const newGroupIdRef = useRef<string | null>(null);
  // Review R3: set when the edited row was deleted by someone else; the
  // notice then offers Discard (editing a ghost row cannot succeed).
  const vanishedRef = useRef(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const sorted = [...layerGroups].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, {numeric: true}),
  );

  // B5c — the shared confirmed-write controller (LayerBatchPage's editor is the
  // reference consumer). It owns the debounce the hand-rolled timer used to
  // approximate, keeps the newest draft until its exact revision is CONFIRMED,
  // and wires the navigation guard: a modal closed over an unconfirmed draft
  // stays put instead of reporting saved. The old optimistic overlay cannot
  // happen: saveLayerGroups publishes only from the seam's confirmed callback.
  const {controller: groupDraft, state: groupSaveState} = useConfirmedDraftAutosave<LayerGroupForm>({
    ownerKey: 'layer.groups.editor',
    debounceMs: 1500,
    // Review F2: a create in flight holds the navigation guard too.
    isBusy: () => creatingRef.current,
    validate: (draft) => {
      if (!editId) return true;
      if (!String(draft.name || '').trim()) {
        setNotice({kind: 'error', message: 'Group name is required.'});
        return false;
      }
      return true;
    },
    persist: (draft) => {
      if (!editId) return Promise.resolve(true);
      return saveLayerGroupsRow(buildLayerGroupRow(draft, editId), false);
    },
  });

  function updLayer(k: keyof LayerGroupForm, v: string) {
    // Review F2: a correction typed while a create is in flight must neither
    // leak into the in-flight write (formRef is frozen for it) nor be silently
    // discarded by the success-path reset.
    if (creatingRef.current) return;
    const f = {...formRef.current, [k]: v};
    formRef.current = f;
    setForm(f);
    // Edit drafts autosave through the controller; a new group (no editId)
    // saves only through the explicit footer button, exactly as before.
    if (editId) groupDraft.update(f);
  }

  async function closeLayerForm() {
    setNotice(null);
    if (editId) {
      // A named autosave modal closes only over CONFIRMED work (C08/Q26 made
      // true rather than assumed): a refused flush leaves the draft and the
      // modal right here, with the failed strip and its Retry button showing.
      if (!(await groupDraft.flush())) return;
    }
    setShowForm(false);
    setEditId(null);
    formRef.current = EMPTY_LG;
    setForm(EMPTY_LG);
  }

  async function save() {
    setNotice(null);
    if (creatingRef.current) return;
    if (!String(form.name || '').trim()) {
      setNotice({kind: 'error', message: 'Group name is required.'});
      return;
    }
    if (editId) {
      // Edit mode: the footer button is the same flush the close path uses.
      if (!(await groupDraft.flush())) {
        setNotice(
          (prev) => prev ?? {kind: 'error', message: 'The group did not save. The form stayed open with your changes.'},
        );
        return;
      }
    } else {
      // Review F2: the in-flight guard blocks a second click (a second
      // whole-document write with a different id), and the frozen form means a
      // correction typed mid-flight is not silently discarded by the reset.
      creatingRef.current = true;
      setCreating(true);
      try {
        // Review N2: the id is minted once per open form and reused, so a
        // retry after a false-fail replaces its own row instead of appending.
        newGroupIdRef.current ??= Date.now().toString();
        const ok = await saveLayerGroupsRow(buildLayerGroupRow(formRef.current, newGroupIdRef.current), true);
        if (!ok) {
          setNotice(
            (prev) =>
              prev ?? {kind: 'error', message: 'The group did not save. The form stayed open with your changes.'},
          );
          return;
        }
      } finally {
        creatingRef.current = false;
        setCreating(false);
      }
    }
    setShowForm(false);
    setEditId(null);
    formRef.current = EMPTY_LG;
    setForm(EMPTY_LG);
  }
  function openEdit(g: LayerGroupRecord) {
    setNotice(null);
    // A draft that has not been confirmed yet belongs to the modal that is
    // already logically open: reopening another row would strand it. Same
    // refusal LayerBatchPage's openEdit makes.
    if (groupDraft.isDirty()) {
      setShowForm(true);
      return;
    }
    const f = {
      ...EMPTY_LG,
      ...g,
      // A stored row may carry the legacy `retired`, a differently-cased
      // value, or no status at all. The <select> below offers exactly `active`
      // and `inactive`, so binding the raw value would render a control with no
      // matching option and no visible state. Normalizing here is display-only:
      // it does not itself dirty the form (the controller's pending draft is
      // empty until the first edit), so opening a legacy row never rewrites it.
      // The row is only persisted as `inactive` once the operator actually
      // edits it, and every READ already treats those legacy values as inactive.
      status: normalizeLayerGroupStatus(g.status),
      originalCount: g.originalCount || '',
      currentCount: g.currentCount || '',
      feedLbs: g.feedLbs || '',
      gritLbs: g.gritLbs || '',
      totalEggs: g.totalEggs || '',
      perLbFeedCost: g.perLbFeedCost || '',
      perLbGritCost: g.perLbGritCost || '',
    };
    formRef.current = f;
    setForm(f);
    setEditId(g.id);
    setShowForm(true);
  }

  // ACTIVE is the single literal `active` — legacy `retired`, absent and
  // unknown statuses are all inactive, and duplicate rows carrying one name fail
  // closed. Partitioning by identity against that one filter keeps the two lists
  // exhaustive: every stored row appears in exactly one of them.
  const active = filterActiveLayerGroups(layerGroups);
  const activeRows = new Set<LayerGroupRecord>(active);
  const inactive = layerGroups.filter((g) => !activeRows.has(g));
  // R06: one palette over the whole canonical list, so a group keeps its color
  // whichever section it sits in.
  const groupPalette = buildLayerGroupPalette(layerGroups);

  const Card = ({g}: {g: LayerGroupRecord}) => {
    const gDailys = recentDailys.filter((d) => d.batch_label === g.name).slice(0, 3);
    const dot = layerGroupColor(groupPalette, g.name);
    const lastFeed = gDailys[0]?.feed_lbs;
    const lastCount = gDailys[0]?.layer_count;
    // String() around a column that is already numeric in SQL: parseInt is
    // DECLARED to take a string and did the same ToString conversion itself.
    const recentMort = gDailys.reduce((s, d) => s + (parseInt(String(d.mortality_count)) || 0), 0);
    // Find which batch occupies this housing
    const occupyingHousing = (layerHousings || []).find((h) => h.housing_name === g.name && h.status === 'active');
    const occupyingBatch = occupyingHousing
      ? (layerBatches || []).find((b) => b.id === occupyingHousing.batch_id)
      : null;
    return (
      <div
        {...openableProps(() => openEdit(g))}
        // R08: radius 18 on --shadow-card.
        style={{
          // R06: the group's own card takes a light tint of its color.
          background: layerGroupTint(dot) ?? 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-card)',
          boxShadow: 'var(--shadow-card)',
          padding: '14px 18px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 20,
        }}
        className="hoverable-tile"
      >
        <div style={{minWidth: 220, flexShrink: 0}}>
          <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
            {dot && <GroupDot color={dot} />}
            <div style={{fontSize: 14, fontWeight: 800, color: 'var(--ink)'}}>{g.name}</div>
            {/* The stored value may be the legacy `retired` or absent; the
                badge shows the operational state it actually resolves to. */}
            <Badge status={isActiveLayerGroupStatus(g.status) ? 'active' : 'inactive'}>
              {layerGroupStatusLabel(g.status)}
            </Badge>
          </div>
          {occupyingBatch && (
            <div style={{marginBottom: 4}}>
              <span className="poultry-meta">
                {occupyingBatch.name}
                {occupyingHousing?.current_count ? ' · ' + occupyingHousing.current_count + ' hens' : ''}
              </span>
            </div>
          )}
          {g.notes && <div style={{fontSize: 11, color: 'var(--ink-muted)'}}>{g.notes}</div>}
        </div>
        <div style={{display: 'flex', gap: 24, flex: 1, flexWrap: 'wrap'}}>
          {[
            ['Current hens', g.currentCount || g.originalCount || '—'],
            ['House', g.house || '—'],
            ['Start date', g.startDate ? fmt(g.startDate) : '—'],
            ['Feed/lb cost', Number(g.perLbFeedCost) > 0 ? '$' + g.perLbFeedCost : '—'],
          ].map(([l, v]) => (
            <div key={l}>
              <div style={{fontSize: 10, color: 'var(--ink-faint)', marginBottom: 1}}>{l}</div>
              <div style={{fontWeight: 600, color: 'var(--ink)', fontSize: 12}}>{v}</div>
            </div>
          ))}
        </div>
        {gDailys.length > 0 && (
          // R02: a divider inside the card is the neutral hairline.
          <div style={{borderLeft: '1px solid var(--hairline)', paddingLeft: 16, flexShrink: 0, fontSize: 11}}>
            <div style={{color: 'var(--ink-muted)', marginBottom: 3}}>Last: {fmt(gDailys[0].date)}</div>
            <div style={{display: 'flex', gap: 8}}>
              {(lastFeed ?? 0) > 0 && (
                <span style={{color: 'var(--text-primary)', fontWeight: 600}}>{lastFeed} lb feed</span>
              )}
              {(lastCount ?? 0) > 0 && <span style={{color: 'var(--ink)'}}>{lastCount} hens</span>}
              {recentMort > 0 && (
                <span style={{color: 'var(--danger-ink)', fontWeight: 600}}>{recentMort} mortality</span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{minHeight: '100vh', background: 'var(--bg-page)'}}
      data-layer-groups-loaded={groupsResource.loading || Boolean(groupsResource.error) ? 'false' : 'true'}
    >
      <Header />
      <div style={{padding: '1rem', maxWidth: 1180, margin: '0 auto'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
          <div>
            <h1 className="poultry-page-title">Layer Groups</h1>
            {/* Review F19: the count moves out of the H1 into the gray summary
                line, so the accessible name is exactly 'Layer Groups'. */}
            <div style={{fontSize: 13, fontWeight: 400, color: 'var(--ink-muted)', marginTop: 2}}>
              {active.length} active · {inactive.length} inactive
            </div>
          </div>
          <Button
            variant="primary"
            onClick={() => {
              setNotice(null);
              // An unconfirmed edit draft keeps its modal: creating a group
              // while the editor holds a refused draft would strand it.
              if (groupDraft.isDirty()) {
                setShowForm(true);
                return;
              }
              setForm(EMPTY_LG);
              formRef.current = EMPTY_LG;
              setEditId(null);
              setShowForm(true);
            }}
          >
            + Add Group
          </Button>
        </div>
        {active.length > 0 && (
          <Collapsible label="Active Groups" count={active.length} stateKey="layer.groups.active">
            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
              {active.map((group) => (
                <Card key={group.id} g={group} />
              ))}
            </div>
          </Collapsible>
        )}
        {inactive.length > 0 && (
          <Collapsible
            label="Inactive Groups"
            count={inactive.length}
            stateKey="layer.groups.inactive"
            defaultOpen={false}
          >
            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
              {inactive.map((group) => (
                <Card key={group.id} g={group} />
              ))}
            </div>
          </Collapsible>
        )}
        {/* Three states where there used to be one. The directory arrived with
            the boot payload, so "empty" and "not loaded yet" were the same
            frame and "unreachable" could not happen. A route-local read makes
            all three real, and reporting a pending or failed read as "No layer
            groups yet." would be the empty-farm lie this migration is supposed
            to remove, not introduce. */}
        {groupsResource.loading && layerGroups.length === 0 && (
          <div
            data-layer-groups-loading
            style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)', fontSize: 13}}
          >
            Loading layer groups…
          </div>
        )}
        {/* Boolean(), not a bare truthiness chain: `error` is `unknown` on the
            resource contract, so the && expression would be `unknown` and could
            not be a ReactNode. */}
        {!groupsResource.loading && Boolean(groupsResource.error) && (
          <div
            data-layer-groups-load-error
            style={{textAlign: 'center', padding: '3rem', color: 'var(--danger-ink)', fontSize: 13}}
          >
            Could not load layer groups.{' '}
            <Button type="button" onClick={() => groupsResource.refetch()}>
              Retry
            </Button>
          </div>
        )}
        {!groupsResource.loading && !groupsResource.error && layerGroups.length === 0 && (
          <div style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)', fontSize: 13}}>
            No layer groups yet.
          </div>
        )}
      </div>
      {showForm && (
        <Modal
          title={editId ? 'Edit Layer Group' : 'New Layer Group'}
          onClose={() => void closeLayerForm()}
          size="md"
          autosave={!!editId}
          footer={
            <ModalFooter
              primaryLabel="Add Group"
              onPrimary={() => void save()}
              busy={groupSaveState === 'saving' || creating}
            />
          }
        >
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
            <div style={{gridColumn: '1/-1'}}>
              <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />
            </div>

            {groupSaveState === 'failed' && (
              <div style={{gridColumn: '1/-1'}}>
                <InlineNotice
                  notice={{kind: 'error', message: 'Changes have not been fully saved. Your draft is still here.'}}
                  action={
                    <Button variant="secondary" onClick={() => void groupDraft.flush()}>
                      Retry Save
                    </Button>
                  }
                />
              </div>
            )}

            <div style={{gridColumn: '1/-1'}}>
              <label style={S.label}>Group Name *</label>
              <input
                value={form.name}
                onChange={(e) => updLayer('name', e.target.value)}
                placeholder="e.g. Eggmobile #2 - 2026"
              />
            </div>

            <div>
              <label style={S.label}>Status</label>
              {/* THE shared operational-status control for every Layer
                    Group. Exactly two choices, persisting `active` or
                    `inactive`; the legacy `retired` value is still readable and
                    still means inactive, but it is no longer writable. */}
              <select value={form.status} onChange={(e) => updLayer('status', e.target.value)}>
                {LAYER_GROUP_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={S.label}>House / Location</label>
              <input value={form.house} onChange={(e) => updLayer('house', e.target.value)} />
            </div>

            <div>
              <label style={S.label}>Start Date</label>
              <input type="date" value={form.startDate} onChange={(e) => updLayer('startDate', e.target.value)} />
            </div>

            <div>
              <label style={S.label}>Original Count</label>
              <input
                type="number"
                min="0"
                value={form.originalCount || ''}
                onChange={(e) => updLayer('originalCount', e.target.value)}
              />
            </div>

            <div>
              <label style={S.label}>Current Count</label>
              <input
                type="number"
                min="0"
                value={form.currentCount || ''}
                onChange={(e) => updLayer('currentCount', e.target.value)}
              />
            </div>

            <div>
              <label style={S.label}>Last Count Date</label>
              <input
                type="date"
                value={form.lastCountDate}
                onChange={(e) => updLayer('lastCountDate', e.target.value)}
              />
            </div>

            <div>
              <label style={S.label}>Total Eggs</label>
              <input
                type="number"
                min="0"
                value={form.totalEggs || ''}
                onChange={(e) => updLayer('totalEggs', e.target.value)}
              />
            </div>

            <div>
              <label style={S.label}>Total Feed Used (lbs)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.feedLbs || ''}
                onChange={(e) => updLayer('feedLbs', e.target.value)}
              />
            </div>

            <div>
              <label style={S.label}>Per Lb Feed Cost ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.perLbFeedCost || ''}
                onChange={(e) => updLayer('perLbFeedCost', e.target.value)}
              />
            </div>

            <div>
              <label style={S.label}>Total Grit Used (lbs)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.gritLbs || ''}
                onChange={(e) => updLayer('gritLbs', e.target.value)}
              />
            </div>

            <div>
              <label style={S.label}>Per Lb Grit Cost ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.perLbGritCost || ''}
                onChange={(e) => updLayer('perLbGritCost', e.target.value)}
              />
            </div>

            <div style={{gridColumn: '1/-1'}}>
              <label style={S.label}>Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => updLayer('notes', e.target.value)}
                rows={3}
                style={{resize: 'vertical'}}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default LayersView;
